<script setup lang="ts">
import { PET_AND_COUPLE_2, USER_AND_CAMPERVAN } from '~/constants/pictures'

const { t } = useI18n()
const { searchCampers } = useSearchCampers()

const { data: products, error } = await useAsyncData('page-rv-hire-with-pets', async () => {
  const [{ results: motorhome }, { results: convertedVan }, { results: campersVan }] = await Promise.all([
    searchCampers({
      page_size: 3,
      types: '1,2,3',
      pets: true,
    }),
    searchCampers({
      page_size: 3,
      types: '4',
      pets: true,
    }),
    searchCampers({
      page_size: 3,
      types: '5',
      pets: true,
    }),
  ])

  return {
    motorhome,
    convertedVan,
    campersVan,
  }
})

if (error.value) {
  throw createError(error.value)
}

const articles = [
  {
    title: t('pages.rv_hire_with_pets.advantages.pets.title'),
    content: t('pages.rv_hire_with_pets.advantages.pets.content'),
    picture: USER_AND_CAMPERVAN,
  },
  {
    title: t('pages.rv_hire_with_pets.advantages.dogs.title'),
    content: t('pages.rv_hire_with_pets.advantages.dogs.content'),
    picture: PET_AND_COUPLE_2,
  },
]

const faqItems = [
  {
    question: t('pages.rv_hire_with_pets.faq.items.borders.question'),
    answer: `<p>${t('pages.rv_hire_with_pets.faq.items.borders.answer')}</p>`,
  },
  {
    question: t('pages.rv_hire_with_pets.faq.items.inside.question'),
    answer: `<p>${t('pages.rv_hire_with_pets.faq.items.inside.answer')}</p>`,
  },
  {
    question: t('pages.rv_hire_with_pets.faq.items.transport.question'),
    answer: `<p>${t('pages.rv_hire_with_pets.faq.items.transport.answer')}</p>`,
  },
  {
    question: t('pages.rv_hire_with_pets.faq.items.species.question'),
    answer: `<p>${t('pages.rv_hire_with_pets.faq.items.species.answer')}</p>`,
  },
  {
    question: t('pages.rv_hire_with_pets.faq.items.convert.question'),
    answer: `<p>${t('pages.rv_hire_with_pets.faq.items.convert.answer')}</p>`,
  },
  {
    question: t('pages.rv_hire_with_pets.faq.items.parking.question'),
    answer: `<p>${t('pages.rv_hire_with_pets.faq.items.parking.answer')}</p>`,
  },
  {
    question: t('pages.rv_hire_with_pets.faq.items.accessories.question'),
    answer: `<p>${t('pages.rv_hire_with_pets.faq.items.accessories.answer')}</p>`,
  },
  {
    question: t('pages.rv_hire_with_pets.faq.items.documents.question'),
    answer: `<p>${t('pages.rv_hire_with_pets.faq.items.documents.answer')}</p>`,
  },
  {
    question: t('pages.rv_hire_with_pets.faq.items.med-kit.question'),
    answer: `<p>${t('pages.rv_hire_with_pets.faq.items.med-kit.answer')}</p>`,
  },
]

useSeoText({ title: t('pages.rv_hire_with_pets.meta.title'), description: t('pages.rv_hire_with_pets.meta.description') })
</script>

<template>
  <div>
    <AppPageHero
      :bg-image="twicpicsPath(PET_AND_COUPLE_2, { isLibrary: true })"
      :extra-query="{ pets: true }"
    >
      <template #title-big>
        {{ $t('pages.rv_hire_with_pets.title') }}
      </template>
      <template #title-seo>
        {{ $t('pages.rv_hire_with_pets.title_seo') }}
      </template>
    </AppPageHero>

    <AppPageSection>
      <AppSectionCampers
        v-if="products?.motorhome"
        :campers="products.motorhome"
        :show-mobile-cta="false"
      >
        <template #title>
          {{ $t('pages.rv_hire_with_pets.motorhome.title') }}
        </template>
        <template #description>
          {{ $t('pages.rv_hire_with_pets.motorhome.content') }}
        </template>
        <template #action>
          <div>
            <NuxtLink
              :to="{ name: 's', query: { types: '1,2,3', pets: 'true' } }"
              class="btn btn-secondary"
            >
              {{ $t('commons.links.search') }}
            </NuxtLink>
          </div>
        </template>
      </AppSectionCampers>
      <AppSectionCampers
        v-if="products?.convertedVan"
        :campers="products.convertedVan"
        :show-mobile-cta="false"
      >
        <template #title>
          {{ $t('pages.rv_hire_with_pets.converted_van.title') }}
        </template>
        <template #description>
          {{ $t('pages.rv_hire_with_pets.converted_van.content') }}
        </template>
        <template #action>
          <div>
            <NuxtLink
              :to="{ name: 's', query: { types: '4', pets: 'true' } }"
              class="btn btn-secondary"
            >
              {{ $t('commons.links.search_converted_van') }}
            </NuxtLink>
          </div>
        </template>
      </AppSectionCampers>
      <AppSectionCampers
        v-if="products?.campersVan"
        :campers="products.campersVan"
        :show-mobile-cta="false"
      >
        <template #title>
          {{ $t('pages.rv_hire_with_pets.camper.title') }}
        </template>
        <template #description>
          {{ $t('pages.rv_hire_with_pets.camper.content') }}
        </template>
        <template #action>
          <div>
            <NuxtLink
              :to="{ name: 's', query: { types: '5', pets: 'true' } }"
              class="btn btn-secondary"
            >
              {{ $t('commons.links.search_camper') }}
            </NuxtLink>
          </div>
        </template>
      </AppSectionCampers>

      <div class="text-center">
        <NuxtLink
          :to="{ name: 's', query: { pets: 'true' } }"
          class="btn btn-primary"
        >
          {{ $t('commons.links.search') }}
        </NuxtLink>
      </div>
    </AppPageSection>
    <AppPageSection>
      <AppPageArticles :articles="articles" />
      <div class="text-center">
        <NuxtLink
          :to="{ name: 's', query: { pets: 'true' } }"
          class="btn btn-primary"
        >
          {{ $t('commons.links.search') }}
        </NuxtLink>
      </div>
    </AppPageSection>

    <AppPageSection>
      <div class="mx-auto max-w-screen-lg space-y-8">
        <h3 class="text-center text-xl font-semibold lg:text-3xl">
          {{ $t('pages.rv_hire_with_pets.faq.title') }}
        </h3>
        <YscCollapseGroup :items="faqItems" />
      </div>
    </AppPageSection>
  </div>
</template>
